import { useAuth, UserTeam, UserGroup } from '@/contexts/AuthContext';

/**
 * Hook for accessing and managing the active team/group context.
 *
 * Returns the active team and a function to change it.
 * The active team is persisted to localStorage.
 *
 * Note: This hook provides backwards compatibility - "group" is now "team"
 */
export function useActiveGroup() {
  const { activeTeam, activeGroup, setActiveTeam, setActiveGroup, user } = useAuth();

  return {
    // New team-based properties
    activeTeam,
    setActiveTeam,
    teams: user?.teams || [],
    hasTeams: (user?.teams?.length || 0) > 0,
    // Backwards compatibility aliases
    activeGroup: activeTeam,
    setActiveGroup,
    groups: user?.teams || [],
    hasGroups: (user?.teams?.length || 0) > 0,
  };
}

/**
 * Hook for accessing and managing the active team context.
 * This is the new preferred hook name.
 */
export function useActiveTeam() {
  const { activeTeam, setActiveTeam, user } = useAuth();

  return {
    activeTeam,
    setActiveTeam,
    teams: user?.teams || [],
    hasTeams: (user?.teams?.length || 0) > 0,
  };
}

/**
 * Get the active team/group ID for API requests.
 * Returns null if no team is selected.
 */
export function getActiveGroupId(): string | null {
  const storedTeamId = localStorage.getItem('greenfirefly_active_team')
    || localStorage.getItem('greenfirefly_active_group');
  return storedTeamId;
}

/**
 * Get the active team ID for API requests (preferred name).
 * Returns null if no team is selected.
 */
export function getActiveTeamId(): string | null {
  return getActiveGroupId();
}

export type { UserTeam, UserGroup };
